import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { 
  FileText, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  Search,
  Clock,
  Server,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';

export default function ApiLogsViewer({ open, onClose }) {
  const [searchTaskId, setSearchTaskId] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: logsData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['api-logs', searchTaskId, limit],
    queryFn: () => {
      const params = { limit };
      if (searchTaskId) params.task_id = searchTaskId;
      return api.get('/logs/api', { params }).then(res => res.data);
    },
    enabled: open,
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-amber-100 text-amber-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'update_task_status': 'Actualizar Estado',
      'add_comment': 'Agregar Comentario',
      'upload_image': 'Subir Imagen',
      'bulk_update': 'Actualización Masiva',
      'sync': 'Sincronización',
      'ai_response': 'Respuesta IA'
    };
    return labels[action] || action;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center text-xl">
            <FileText className="w-6 h-6 mr-2 text-primary" />
            Registro de Llamadas API
          </DialogTitle>
          <DialogDescription>
            Historial de todas las interacciones con la plataforma municipal SCMejora
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex items-center gap-3 py-3 border-b">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por ID de tarea..."
              value={searchTaskId}
              onChange={(e) => setSearchTaskId(e.target.value)}
              className="pl-10"
              data-testid="logs-search-input"
            />
          </div>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="h-10 px-3 border rounded-md bg-background"
            data-testid="logs-limit-select"
          >
            <option value={25}>25 registros</option>
            <option value={50}>50 registros</option>
            <option value={100}>100 registros</option>
          </select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isRefetching}
            data-testid="logs-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Stats Summary */}
        {logsData && (
          <div className="flex items-center gap-4 py-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Server className="w-4 h-4" />
              {logsData.total} registros
            </span>
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              {logsData.logs?.filter(l => l.success).length || 0} exitosos
            </span>
            <span className="flex items-center gap-1 text-red-600">
              <XCircle className="w-4 h-4" />
              {logsData.logs?.filter(l => !l.success).length || 0} fallidos
            </span>
          </div>
        )}

        {/* Logs List */}
        <ScrollArea className="flex-1 pr-4">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : logsData?.logs?.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay registros de API disponibles</p>
              <p className="text-sm mt-1">Los registros aparecerán cuando se realicen acciones en las tareas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logsData?.logs?.map((log, index) => (
                <Card 
                  key={log._id || index} 
                  className={`overflow-hidden border-l-4 ${log.success ? 'border-l-green-500' : 'border-l-red-500'}`}
                  data-testid={`api-log-item-${index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left side - Main info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getMethodColor(log.method)}>
                            {log.method?.toUpperCase() || 'N/A'}
                          </Badge>
                          <Badge variant="outline">
                            {getActionLabel(log.action)}
                          </Badge>
                          {log.task_id && (
                            <Badge variant="secondary">
                              Tarea #{log.task_id}
                            </Badge>
                          )}
                          {log.success ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>

                        {/* Endpoint */}
                        <div className="flex items-center gap-2 text-sm">
                          <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                          <code className="text-xs bg-muted px-2 py-1 rounded max-w-md truncate">
                            {log.endpoint}
                          </code>
                        </div>

                        {/* Response Status */}
                        {log.response_status && (
                          <div className="flex items-center gap-2 text-sm">
                            <ArrowDownLeft className="w-4 h-4 text-muted-foreground" />
                            <span className={`font-mono ${log.response_status >= 200 && log.response_status < 300 ? 'text-green-600' : 'text-red-600'}`}>
                              HTTP {log.response_status}
                            </span>
                          </div>
                        )}

                        {/* Error message if failed */}
                        {!log.success && log.error && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                            <strong>Error:</strong> {log.error}
                          </div>
                        )}

                        {/* Payload preview */}
                        {log.payload && (
                          <details className="mt-2">
                            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                              Ver payload enviado
                            </summary>
                            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto max-h-32">
                              {JSON.stringify(log.payload, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>

                      {/* Right side - Timestamp */}
                      <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {formatDate(log.timestamp)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
